import React, { useEffect } from "react";

interface ShortcutsOverlayProps {
  open: boolean;
  onClose: () => void;
}

interface ShortcutGroup {
  title: string;
  entries: { keys: string[]; description: string }[];
}

const GROUPS: ShortcutGroup[] = [
  {
    title: "Navigation",
    entries: [
      { keys: ["j"], description: "Next card" },
      { keys: ["k"], description: "Previous card" },
      { keys: ["h"], description: "Move focus to previous column" },
      { keys: ["l"], description: "Move focus to next column" },
      { keys: ["enter"], description: "Open focused card" },
      { keys: ["esc"], description: "Close panel / cancel" }
    ]
  },
  {
    title: "Actions",
    entries: [
      { keys: ["c"], description: "Create issue in the hovered column" },
      { keys: ["shift", "j"], description: "Move focused card to next column" },
      { keys: ["shift", "k"], description: "Move focused card to previous column" },
      { keys: ["a"], description: "Assign focused card to me" },
      { keys: ["w"], description: "Watch / unwatch focused card" },
      { keys: ["1–5"], description: "Set priority on focused card" },
      { keys: ["x"], description: "Archive focused card" },
      { keys: ["?"], description: "Toggle shortcut cheatsheet" }
    ]
  },
  {
    title: "Go to",
    entries: [
      { keys: ["g", "b"], description: "Board view" },
      { keys: ["g", "l"], description: "Backlog view" },
      { keys: ["g", "r"], description: "Roadmap view" }
    ]
  },
  {
    title: "Power",
    entries: [
      { keys: ["⌘", "k"], description: "Command palette" },
      { keys: ["⌘", "b"], description: "Switch to next board" },
      { keys: ["⌘", "."], description: "Focus filter" },
      { keys: ["/"], description: "Focus filter (no modifier)" }
    ]
  }
];

const ShortcutsOverlay: React.FC<ShortcutsOverlayProps> = ({ open, onClose }) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape" || event.key === "?") {
        event.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="kuika-board__shortcuts-overlay" onClick={onClose} role="dialog" aria-modal="true">
      <div className="kuika-board__shortcuts-panel" onClick={(event: React.MouseEvent) => event.stopPropagation()}>
        <div className="kuika-board__shortcuts-title">Keyboard shortcuts</div>
        {GROUPS.map((group: ShortcutGroup) => (
          <div key={group.title} className="kuika-board__shortcuts-group">
            <div className="kuika-board__shortcuts-group-title">{group.title}</div>
            {group.entries.map((entry, index: number) => (
              <div key={index} className="kuika-board__shortcut-row">
                <span>{entry.description}</span>
                <span>
                  {entry.keys.map((key: string, keyIndex: number) => (
                    <React.Fragment key={key}>
                      <kbd className="kuika-board__kbd">{key}</kbd>
                      {keyIndex < entry.keys.length - 1 && " + "}
                    </React.Fragment>
                  ))}
                </span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ShortcutsOverlay;
